'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import Badge from '@/components/ui/Badge';
import Toggle from '@/components/ui/Toggle';
import Modal from '@/components/ui/Modal';
import JobTimeline from './JobTimeline';
import CompletionReportViewer from './CompletionReportViewer';
import QuoteAcceptanceCard from './QuoteAcceptanceCard';
import { createClient } from '@/lib/supabase/client';

export interface Job {
  id: string;
  refNumber: string;
  serviceType: string;
  serviceSubType?: string;
  address: string;
  status: 'quoted' | 'accepted' | 'scheduled' | 'inprogress' | 'completed' | 'declined';
  scheduledDate?: string;
  scheduledTime?: string;
  assignedTeam?: string;
  assignedMembers?: string[];
  quoteTotal?: number;
  paymentStatus?: 'unpaid' | 'invoiced' | 'paid';
  hasCompletionReport?: boolean;
  completionDate?: string;
  completionNotes?: string;
  completionPhotos?: string[];
  notes?: string;
}

type TabId = 'upcoming' | 'active' | 'past';

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: 'upcoming', label: 'Upcoming', icon: 'CalendarDaysIcon' },
  { id: 'active', label: 'Active', icon: 'BoltIcon' },
  { id: 'past', label: 'Past Jobs', icon: 'ArchiveBoxIcon' },
];

const statusToTab: Record<Job['status'], TabId> = {
  quoted: 'upcoming',
  accepted: 'upcoming',
  scheduled: 'upcoming',
  inprogress: 'active',
  completed: 'past',
  declined: 'past',
};

function mapRow(row: any): Job {
  return {
    id: row.id,
    refNumber: row.ref_number ?? row.id.slice(0, 8).toUpperCase(),
    serviceType: row.service_type ?? '',
    serviceSubType: row.service_sub_type ?? undefined,
    address: row.address ?? '',
    status: row.status ?? 'quoted',
    scheduledDate: row.scheduled_date ?? undefined,
    scheduledTime: row.scheduled_time ?? undefined,
    assignedTeam: row.assigned_team ?? undefined,
    assignedMembers: row.assigned_members ?? undefined,
    quoteTotal: row.quote_total ?? undefined,
    paymentStatus: row.payment_status ?? undefined,
    hasCompletionReport: row.has_completion_report ?? false,
    completionDate: row.completion_date ?? undefined,
    completionNotes: row.completion_notes ?? undefined,
    completionPhotos: row.completion_photos ?? undefined,
    notes: row.notes ?? undefined,
  };
}

export default function ClientJobPortalContent() {
  const [activeTab, setActiveTab] = useState<TabId>('upcoming');
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [notifUpdates, setNotifUpdates] = useState(true);
  const [specialOffers, setSpecialOffers] = useState(false);
  const [reportJobId, setReportJobId] = useState<string | null>(null);
  const [acceptJobId, setAcceptJobId] = useState<string | null>(null);
  const [declineJobId, setDeclineJobId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientName, setClientName] = useState('');
  const [liveIndicator, setLiveIndicator] = useState(false);

  const supabase = createClient();

  const fetchJobs = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from('jobs')
      .select('*')
      .eq('client_id', userId)
      .order('created_at', { ascending: false });

    if (!error && data) {
      setJobs(data.map(mapRow));
    }
  }, []);

  useEffect(() => {
    let userId = '';

    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        userId = user.id;
        const displayName = user.user_metadata?.full_name
          || user.email?.split('@')[0]
          || 'there';
        setClientName(displayName);
        await fetchJobs(userId);
      }

      setLoading(false);
    };

    // Run async init first, then set up realtime subscription synchronously
    let channel: ReturnType<typeof supabase.channel> | null = null;

    init().then(() => {
      // Remove any existing channel with the same name to avoid duplicate subscriptions
      supabase.getChannels().forEach((ch) => {
        if (ch.topic === 'realtime:client-portal-jobs') {
          supabase.removeChannel(ch);
        }
      });

      channel = supabase
        .channel('client-portal-jobs')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'jobs',
            filter: userId ? `client_id=eq.${userId}` : undefined,
          },
          (payload) => {
            if (payload.eventType === 'UPDATE') {
              setJobs((prev) =>
                prev.map((j) =>
                  j.id === payload.new.id ? { ...j, ...mapRow(payload.new) } : j
                )
              );
              setLiveIndicator(true);
              setTimeout(() => setLiveIndicator(false), 2500);
            } else if (payload.eventType === 'INSERT') {
              setJobs((prev) => [mapRow(payload.new), ...prev]);
              setLiveIndicator(true);
              setTimeout(() => setLiveIndicator(false), 2500);
            } else if (payload.eventType === 'DELETE') {
              setJobs((prev) => prev.filter((j) => j.id !== payload.old.id));
            }
          }
        )
        .subscribe();
    });

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [fetchJobs]);

  const filteredJobs = jobs.filter((j) => statusToTab[j.status] === activeTab);

  const tabCounts: Record<TabId, number> = {
    upcoming: jobs.filter((j) => statusToTab[j.status] === 'upcoming').length,
    active: jobs.filter((j) => statusToTab[j.status] === 'active').length,
    past: jobs.filter((j) => statusToTab[j.status] === 'past').length,
  };

  const handleAcceptQuote = async (jobId: string) => {
    await supabase.from('jobs').update({ status: 'accepted' }).eq('id', jobId);
    setJobs((prev) =>
      prev.map((j) => (j.id === jobId ? { ...j, status: 'accepted' as const } : j))
    );
    setAcceptJobId(null);
  };

  const handleDeclineQuote = async (jobId: string) => {
    await supabase.from('jobs').update({ status: 'declined' }).eq('id', jobId);
    setJobs((prev) =>
      prev.map((j) => (j.id === jobId ? { ...j, status: 'declined' as const } : j))
    );
    setDeclineJobId(null);
  };

  const reportJob = jobs.find((j) => j.id === reportJobId);
  const acceptJob = jobs.find((j) => j.id === acceptJobId);
  const declineJob = jobs.find((j) => j.id === declineJobId);

  const paymentBadge = (status?: string) => {
    if (status === 'paid') return <span className="badge-status-completed inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium">Paid</span>;
    if (status === 'invoiced') return <span className="badge-status-quoted inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium">Invoice Sent</span>;
    return <span className="badge-severity-general inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium">Unpaid</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">My Jobs</h1>
            {/* Live indicator */}
            <span
              className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium transition-all duration-500 ${
                liveIndicator
                  ? 'bg-green-100 text-green-700 scale-105' :'bg-muted text-muted-foreground'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  liveIndicator ? 'bg-green-500 animate-pulse' : 'bg-muted-foreground/50'
                }`}
              />
              Live
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {clientName
              ? `Welcome back, ${clientName}. Here's everything about your service bookings.` : "Here's everything about your service bookings."}
          </p>
        </div>
        <a
          href="/"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 active:scale-95 self-start sm:self-auto"
        >
          <Icon name="PlusIcon" size={16} variant="outline" />
          Request New Service
        </a>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Jobs', value: jobs.length.toString(), icon: 'BriefcaseIcon', color: 'text-foreground' },
          { label: 'Quotes Pending', value: jobs.filter((j) => j.status === 'quoted').length.toString(), icon: 'ClockIcon', color: 'text-amber-600' },
          { label: 'Jobs Active', value: jobs.filter((j) => j.status === 'inprogress' || j.status === 'scheduled').length.toString(), icon: 'BoltIcon', color: 'text-blue-600' },
          { label: 'Completed', value: jobs.filter((j) => j.status === 'completed').length.toString(), icon: 'CheckCircleIcon', color: 'text-primary' },
        ].map((stat) => (
          <div key={`stat-${stat.label.replace(/\s/g, '-')}`} className="card-surface p-4 flex items-center gap-3">
            <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name={stat.icon as any} size={18} variant="outline" className={stat.color} />
            </div>
            <div>
              <p className="text-xl font-bold text-foreground font-tabular">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Jobs list */}
        <div className="xl:col-span-2 space-y-4">
          {/* Tabs */}
          <div className="card-surface p-1 flex gap-1">
            {tabs.map((tab) => (
              <button
                key={`tab-${tab.id}`}
                type="button"
                onClick={() => { setActiveTab(tab.id); setExpandedJob(null); }}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <Icon name={tab.icon as any} size={15} variant="outline" />
                {tab.label}
                {tabCounts[tab.id] > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                    {tabCounts[tab.id]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Loading state */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="card-surface p-4 animate-pulse">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-muted rounded-lg flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-1/3" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                      <div className="h-3 bg-muted rounded w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="card-surface p-10 text-center space-y-3">
              <Icon name="BriefcaseIcon" size={36} variant="outline" className="mx-auto text-muted-foreground" />
              <p className="text-sm font-semibold text-foreground">No {activeTab} jobs</p>
              <p className="text-xs text-muted-foreground">
                {activeTab === 'upcoming'
                  ? "You don't have any upcoming jobs or pending quotes. Request a new service to get started."
                  : activeTab === 'active'
                  ? "No jobs are currently in progress." :"Your completed job history will appear here."}
              </p>
              {activeTab === 'upcoming' && (
                <a href="/" className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all mt-2">
                  <Icon name="PlusIcon" size={15} variant="outline" />
                  Request a Service
                </a>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredJobs.map((job) => {
                const isExpanded = expandedJob === job.id;
                return (
                  <div key={job.id} className="card-surface overflow-hidden">
                    {/* Job card header */}
                    <button
                      type="button"
                      onClick={() => setExpandedJob(isExpanded ? null : job.id)}
                      className="w-full text-left p-4 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="w-9 h-9 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon
                              name={
                                job.serviceType === 'Cleaning' ? 'SparklesIcon'
                                  : job.serviceType === 'Gardening & Lawn' ? 'SunIcon' : 'WrenchScrewdriverIcon'
                              }
                              size={18}
                              variant="outline"
                              className="text-primary"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-foreground">{job.serviceType}</p>
                              <Badge variant={job.status as any}>
                                {job.status === 'inprogress' ? 'In Progress' : job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                              </Badge>
                              {job.status === 'quoted' && (
                                <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                                  <Icon name="ExclamationTriangleIcon" size={12} variant="outline" />
                                  Action needed
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">{job.serviceSubType}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                              <Icon name="MapPinIcon" size={11} variant="outline" />
                              {job.address}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <p className="text-sm font-bold text-foreground font-tabular">
                            ${job.quoteTotal?.toFixed(2)}
                          </p>
                          {paymentBadge(job.paymentStatus)}
                          <Icon
                            name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                            size={16}
                            variant="outline"
                            className="text-muted-foreground mt-1"
                          />
                        </div>
                      </div>

                      {job.scheduledDate && (
                        <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground">
                          <Icon name="CalendarDaysIcon" size={12} variant="outline" />
                          <span>{job.scheduledDate}</span>
                          {job.scheduledTime && <span>· {job.scheduledTime}</span>}
                        </div>
                      )}
                    </button>

                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="border-t border-border px-4 pb-4 pt-4 space-y-4 animate-slide-up">
                        {/* Quote pending — show accept/decline */}
                        {job.status === 'quoted' && (
                          <QuoteAcceptanceCard
                            job={job}
                            onAccept={() => setAcceptJobId(job.id)}
                            onDecline={() => setDeclineJobId(job.id)}
                          />
                        )}

                        {/* Timeline */}
                        {job.status !== 'quoted' && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Job Progress</p>
                            <JobTimeline status={job.status} />
                          </div>
                        )}

                        {/* Team info */}
                        {job.assignedTeam && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Assigned Team</p>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center">
                                <Icon name="UserGroupIcon" size={15} variant="outline" className="text-primary" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{job.assignedTeam}</p>
                                {job.assignedMembers && (
                                  <p className="text-xs text-muted-foreground">{job.assignedMembers.join(', ')}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Completion report */}
                        {job.hasCompletionReport && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Completion Report</p>
                            <button
                              type="button"
                              onClick={() => setReportJobId(job.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-secondary text-primary text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 border border-primary/20"
                            >
                              <Icon name="DocumentTextIcon" size={15} variant="outline" />
                              View Completion Report & Photos
                            </button>
                          </div>
                        )}

                        {/* Notes */}
                        {job.notes && (
                          <div className="bg-muted/30 rounded-lg px-3 py-2">
                            <p className="text-xs text-muted-foreground">{job.notes}</p>
                          </div>
                        )}

                        {/* Ref */}
                        <p className="text-xs text-muted-foreground">Ref: {job.refNumber}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right sidebar — notification prefs */}
        <div className="space-y-4">
          <div className="card-surface p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Icon name="BellIcon" size={16} variant="outline" className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Notification Preferences</h3>
            </div>
            <div className="space-y-4">
              <Toggle
                id="portal-notif-updates"
                checked={notifUpdates}
                onChange={setNotifUpdates}
                label="Job update notifications"
                description="Status changes, scheduling confirmations, team on-the-way alerts, and completion notices."
              />
              <Toggle
                id="portal-special-offers"
                checked={specialOffers}
                onChange={setSpecialOffers}
                label="Special offers & promotions"
                description="Seasonal discounts and service packages. Unsubscribe any time."
              />
            </div>
            <button
              type="button"
              className="w-full px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-lg hover:opacity-90 transition-all active:scale-95"
            >
              Save Preferences
            </button>
          </div>

          <div className="card-surface p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="UserCircleIcon" size={16} variant="outline" className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Account Details</h3>
            </div>
            <div className="space-y-2 text-xs">
              {[
                { label: 'Name', value: clientName || 'Hayley Thornton' },
                { label: 'Email', value: 'hayley.thornton@gmail.com' },
                { label: 'Phone', value: '0412 345 678' },
                { label: 'Member since', value: 'June 2026' },
              ].map((row) => (
                <div key={`acct-${row.label.replace(/\s/g, '-')}`} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-medium text-foreground text-right">{row.value}</span>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="w-full px-4 py-2 border border-border text-xs font-medium text-muted-foreground rounded-lg hover:bg-muted hover:text-foreground transition-all"
            >
              Edit Profile
            </button>
          </div>

          <div className="card-surface p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Icon name="PhoneIcon" size={16} variant="outline" className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Need help?</h3>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Have a question about your booking? Our team is available Mon–Fri 7am–5pm.
            </p>
            <div className="space-y-1.5">
              <a href="tel:1300SERVIQ" className="flex items-center gap-2 text-xs text-primary font-medium hover:underline">
                <Icon name="PhoneIcon" size={13} variant="outline" />
                1300 SERVI Q
              </a>
              <a href="mailto:hello@serviquote.com.au" className="flex items-center gap-2 text-xs text-primary font-medium hover:underline">
                <Icon name="EnvelopeIcon" size={13} variant="outline" />
                hello@serviquote.com.au
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Completion report modal */}
      <Modal
        isOpen={!!reportJobId}
        onClose={() => setReportJobId(null)}
        title={`Completion Report — ${reportJob?.refNumber ?? ''}`}
        size="xl"
      >
        {reportJob && <CompletionReportViewer job={reportJob} />}
      </Modal>

      {/* Accept quote modal */}
      <Modal
        isOpen={!!acceptJobId}
        onClose={() => setAcceptJobId(null)}
        title="Accept Quote"
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => setAcceptJobId(null)}
              className="px-4 py-2 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => acceptJob && handleAcceptQuote(acceptJob.id)}
              className="px-5 py-2 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all active:scale-95"
            >
              Confirm & Accept Quote
            </button>
          </>
        }
      >
        {acceptJob && (
          <div className="space-y-4">
            <div className="bg-secondary rounded-xl p-4">
              <p className="text-sm font-semibold text-primary">{acceptJob.serviceType}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{acceptJob.serviceSubType}</p>
              <p className="text-lg font-bold text-foreground mt-2 font-tabular">
                ${acceptJob.quoteTotal?.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">inc. GST</span>
              </p>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              By accepting this quote you're confirming you'd like to proceed with this service. Our team will contact you within 1 business day to confirm your scheduled date and time.
            </p>
            <p className="text-xs text-muted-foreground">
              A login to your ServiQuote client portal will be created (or updated) and sent to your email address so you can track your job.
            </p>
          </div>
        )}
      </Modal>

      {/* Decline quote modal */}
      <Modal
        isOpen={!!declineJobId}
        onClose={() => setDeclineJobId(null)}
        title="Decline Quote"
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setDeclineJobId(null)}
              className="px-4 py-2 border border-border text-sm font-medium text-muted-foreground rounded-lg hover:bg-muted transition-colors"
            >
              Keep Quote
            </button>
            <button
              type="button"
              onClick={() => declineJob && handleDeclineQuote(declineJob.id)}
              className="px-5 py-2 bg-red-500 text-white text-sm font-semibold rounded-lg hover:bg-red-600 transition-all active:scale-95"
            >
              Decline Quote
            </button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground leading-relaxed">
          Are you sure you want to decline this quote? This action cannot be undone. If you'd like a revised quote, please contact us directly.
        </p>
      </Modal>
    </div>
  );
}